import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { Select, Spin, Badge, Tag, Tooltip } from 'antd';
import { GlobalOutlined } from '@ant-design/icons';
import { ChevronDown, Check, Search } from 'lucide-react';
import debounce from 'lodash/debounce';
import { ElasticsearchService } from '@client/services/elasticsearchService';
import { HtmlContent } from '@client/utils/html_content';
import DownIcon from '../../../../img/ant-down-icon.svg';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from '@client/hooks/useIsMobile';
import MobileSheetBase from '@client/components/mobile/MobileSheetBase';
import CollapsibleTags from './CollapsibleTags';

/**
 * @typedef {Object} ElasticsearchOption
 * @property {string} label - Display label
 * @property {string} value - Option value
 * @property {number} [count] - Result count
 * @property {boolean} [highlighted] - Highlight status
 */

/**
 * @typedef {Object} ElasticsearchSelectProps
 * @property {string} document - Document type for Elasticsearch
 * @property {string} field - Field to search in
 * @property {string} [label] - Label translation key
 * @property {number} [debounceTimeout] - Search debounce in ms
 * @property {number} [pageSize] - Results per page
 * @property {string} [sortBy] - Sort field
 * @property {string} [sortOrder] - Sort direction
 * @property {boolean} [showCount] - Display result counts
 * @property {boolean} [infinite] - Enable infinite scroll
 * @property {any} value - Selected value(s)
 * @property {Function} onChange - Change handler
 * @property {boolean} [showSelectedTags] - Show selected items as tags
 * @property {string} [value_field] - Value field mapping
 * @property {string} [label_field] - Label field mapping
 * @property {Object} [languageFields] - Language field mappings
 * @property {Object} [languageLabels] - Language display labels
 * @property {string} [defaultLanguage] - Default language code
 * @property {boolean} [showLanguageSelector] - Show language switcher
 * @property {Function} [labelTransform] - Function to transform option labels (label, value) => transformedLabel
 */

const CONFIG = {
  DEBOUNCE_TIMEOUT: 150,
  PAGE_SIZE: 20,
  SORT_BY: 'count',
  SORT_ORDER: 'desc',
  SCROLL_THRESHOLD: 0,
  STORAGE_PREFIX: 'lang_',
};

const TRANSLATIONS = {
  LOADING: 'Chargement...',
  NO_RESULTS: 'Aucun résultat trouvé',
  SEARCH: 'Rechercher...',
  SELECT: 'Sélectionner',
};

/**
 * Strips HTML tags from string
 * @param {string} html - HTML string
 * @returns {string} Plain text
 */
const stripHtml = (html) => {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent || '';
};

/**
 * Option renderer component
 * @param {Object} props
 * @param {ElasticsearchOption} props.option - Option data
 * @param {boolean} props.showCount - Show count badge
 * @returns {JSX.Element}
 */
const OptionRenderer = ({ option, showCount }) => (
  <div className="elasticsearch-option">
    <span
      className={`option-label ${option.data?.highlighted ? 'highlighted' : ''}`}
    >
      <HtmlContent html={option.label} />
    </span>
    {showCount && option.data?.count && (
      <Badge count={option.data.count} className="option-count" />
    )}
  </div>
);


/**
 * Language selector component
 * @param {Object} props
 * @param {string} props.selectedLanguage - Current language
 * @param {Object} props.languageLabels - Language options
 * @param {Function} props.onChange - Change handler
 * @returns {JSX.Element}
 */
const LanguageSelector = ({ selectedLanguage, languageLabels, onChange }) => (
  <Select
    size="small"
    value={selectedLanguage}
    onChange={onChange}
    options={Object.entries(languageLabels).map(([code, label]) => ({
      value: code,
      label: label,
    }))}
    className="language-selector"
    suffixIcon={<GlobalOutlined />}
  />
);

/**
 * Elasticsearch-powered select component with infinite scroll
 * @component
 * @param document
 * @param label
 * @param field
 * @param debounceTimeout
 * @param pageSize
 * @param sortBy
 * @param sortOrder
 * @param showCount
 * @param infinite
 * @param value
 * @param onChange
 * @param showSelectedTags
 * @param value_field
 * @param label_field
 * @param languageFields
 * @param languageLabels
 * @param defaultLanguage
 * @param showLanguageSelector
 * @param {ElasticsearchSelectProps} props
 * @returns {JSX.Element}
 */
const ElasticsearchSelect = React.forwardRef(({
  document,
  label = 'common|Select',
  field,
  debounceTimeout = CONFIG.DEBOUNCE_TIMEOUT,
  pageSize = CONFIG.PAGE_SIZE,
  sortBy = CONFIG.SORT_BY,
  sortOrder = CONFIG.SORT_ORDER,
  showCount = true,
  infinite = true,
  value,
  onChange,
  showSelectedTags = true,
  value_field = null,
  label_field = null,
  languageFields = null,
  languageLabels = null,
  defaultLanguage = 'fr',
  showLanguageSelector = false,
  labelTransform = null,
  filters = null,
  mode = 'multiple',
  placeholder,
  initialLabels = null, // Labels for cold start restoration [{value, label}, ...]
  maxVisibleTags = 1,
  ...props
}, ref) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [options, setOptions] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [sheetOpen, setSheetOpen] = useState(false);
  const [mobileSearchValue, setMobileSearchValue] = useState('');
  const [localValue, setLocalValue] = useState([]);
  const [selectedLanguage, setSelectedLanguage] = useState(() => {
    if (!languageFields) return null;
    const saved = localStorage.getItem(`${CONFIG.STORAGE_PREFIX}${field}`);
    return saved || defaultLanguage;
  });

  const currentLabelField = useMemo(() => {
    return languageFields?.[selectedLanguage] || label_field;
  }, [languageFields, selectedLanguage, label_field]);

  /**
   * Build a label map from initialLabels and options for display purposes
   * Options take precedence over initialLabels
   */
  const labelMap = useMemo(() => {
    const map = new Map();
    // Add initialLabels first (fallback)
    if (Array.isArray(initialLabels)) {
      initialLabels.forEach((item) => {
        if (item?.value) {
          map.set(item.value, item.label || item.name || item.value);
        }
      });
    }
    // Add options (takes precedence)
    options.forEach((opt) => {
      if (opt?.value) {
        map.set(opt.value, opt.label);
      }
    });
    return map;
  }, [initialLabels, options]);

  /**
   * Get display label for a value
   */
  const getLabel = useCallback((val) => {
    return labelMap.get(val) || val;
  }, [labelMap]);

  /**
   * Fetches search results from Elasticsearch
   * @param {string} query - Search query
   * @param {number} page - Page number
   * @param {boolean} append - Append to existing results
   */
  const fetchResults = useCallback(
    async (query, page, append = false) => {
      if (!document || !field) return;

      const isNewSearch = !append;
      setLoading(!isNewSearch);
      setSearching(isNewSearch);

      try {
        const response = await ElasticsearchService.search({
          document,
          field,
          q: query || '',
          page,
          size: pageSize,
          sort: sortBy,
          order: sortOrder,
          value_field,
          label_field: currentLabelField,
          ...filters,
        });

        const formattedOptions = response.options.map((option) => ({
          label: labelTransform ? labelTransform(option.label, option.value) : option.label,
          value: option.value,
          data: {
            count: option.count,
            highlighted: option.highlighted,
          },
        }));

        setOptions((prev) =>
          append ? [...prev, ...formattedOptions] : formattedOptions
        );
        setHasMore(response.pagination.has_more);
        setCurrentPage(page);
      } catch (error) {
        console.error('ElasticsearchSelect fetch error:', error);
        if (!append) setOptions([]);
        setHasMore(false);
      } finally {
        setLoading(false);
        setSearching(false);
      }
    },
    [
      document,
      field,
      pageSize,
      sortBy,
      sortOrder,
      value_field,
      currentLabelField,
      labelTransform,
      filters,
    ]
  );

  /**
   * Handles scroll event for infinite loading
   * @param {Event} event - Scroll event
   */
  const handleScroll = useCallback(
    async (event) => {
      if (!infinite || !hasMore || loading) {
        return;
      }

      const { target } = event;
      const atBottom =
        target.scrollTop + target.offsetHeight >= target.scrollHeight - 5;

      if (atBottom) {
        const nextPage = currentPage + 1;
        await fetchResults(searchQuery, nextPage, true);
      }
    },
    [infinite, hasMore, loading, currentPage, searchQuery, fetchResults]
  );

  /**
   * Debounced search handler
   */
  const debouncedSearch = useMemo(
    () =>
      debounce((query) => {
        setSearchQuery(query);
        setCurrentPage(1);
        fetchResults(query, 1, false);
      }, debounceTimeout),
    [fetchResults, debounceTimeout]
  );

  /**
   * Handles language change
   * @param {string} lang - Language code
   */
  const handleLanguageChange = useCallback(
    (lang) => {
      setSelectedLanguage(lang);
      localStorage.setItem(`${CONFIG.STORAGE_PREFIX}${field}`, lang);
      setOptions([]);
      setCurrentPage(1);
      setSearchQuery('');
      setHasMore(true);
    },
    [field]
  );

  /**
   * Handles dropdown visibility change
   * @param {boolean} open - Visibility state
   */
  const handleDropdownVisibleChange = useCallback(
    (open) => {
      if (open && options.length === 0 && !loading) {
        fetchResults('', 1, false);
      } else if (!open && searchQuery) {
        // Reset search when dropdown closes to avoid confusion
        setSearchQuery('');
        setCurrentPage(1);
        fetchResults('', 1, false);
      }
    },
    [options.length, loading, fetchResults, searchQuery]
  );

  /**
   * Handles tag removal
   * @param {string} removedValue - Value to remove
   */
  const handleRemoveTag = useCallback(
    (removedValue) => {
      const newValue = Array.isArray(value)
        ? value.filter((v) => v !== removedValue)
        : undefined;
      onChange?.(newValue);
    },
    [value, onChange]
  );

  /**
   * Handles value change
   * @param {any} newValue - New value(s)
   */
  const handleChange = useCallback(
    (newValue) => {
      // Normalize to array for consistent handling
      const valueArray = Array.isArray(newValue) ? newValue : (newValue ? [newValue] : []);
      const selectedItems = valueArray.map((val) => ({
        value: val,
        label: getLabel(val),
      }));

      // Return in same format as received (single value for combobox, array for multiple)
      const isMultiple = mode === 'multiple';
      const returnValue = isMultiple ? valueArray : valueArray[0] || null;
      onChange(returnValue, selectedItems);
    },
    [getLabel, onChange, mode]
  );

  useEffect(() => {
    if (languageFields && selectedLanguage) {
      setOptions([]);
      setCurrentPage(1);
      setSearchQuery('');
      setHasMore(true);
      fetchResults('', 1);
    }
  }, [currentLabelField]);

  useEffect(() => {
    if (value_field && label_field && Array.isArray(value) && value.length > 0 && options.length === 0) {
      fetchResults('', 1, false);
    }
  }, []);

  useEffect(() => {
    if (!languageFields) return;

    const handleExternalLanguageChange = (event) => {
      handleLanguageChange(event.detail);
    };

    const eventName = `languageChanged_${field}`;
    window.addEventListener(eventName, handleExternalLanguageChange);

    return () => {
      window.removeEventListener(eventName, handleExternalLanguageChange);
    };
  }, [field, languageFields, handleLanguageChange]);

  const selectOptions = useMemo(() => {
    const processedOptions = options.map((option) => ({
      ...option,
      label: option.label,
      value: option.value,
    }));

    if (loading && infinite) {
      processedOptions.push({
        value: '__loading__',
        label: TRANSLATIONS.LOADING,
        disabled: true,
      });
    }

    return processedOptions;
  }, [options, loading, infinite]);

  // Mobile: Sync localValue with value when sheet opens
  useEffect(() => {
    if (sheetOpen) {
      const valueArray = Array.isArray(value) ? value : (value ? [value] : []);
      setLocalValue(valueArray.filter(Boolean));
      setMobileSearchValue('');
      // Fetch options if not already loaded
      if (options.length === 0 && !loading) {
        fetchResults('', 1, false);
      }
    }
  }, [sheetOpen]);

  // Mobile: Debounced search for sheet
  const debouncedMobileSearch = useMemo(
    () =>
      debounce((query) => {
        setCurrentPage(1);
        fetchResults(query, 1, false);
      }, debounceTimeout),
    [fetchResults, debounceTimeout]
  );

  const handleMobileSearchChange = useCallback((e) => {
    const query = e.target.value;
    setMobileSearchValue(query);
    debouncedMobileSearch(query);
  }, [debouncedMobileSearch]);

  // Mobile: Toggle selection
  const handleMobileSelect = useCallback((optionValue) => {
    const isMultiple = mode === 'multiple';
    if (isMultiple) {
      setLocalValue((prev) => {
        if (prev.includes(optionValue)) {
          return prev.filter((v) => v !== optionValue);
        }
        return [...prev, optionValue];
      });
    } else {
      // Single select - apply immediately and close
      const selectedItem = { value: optionValue, label: getLabel(optionValue) };
      onChange([optionValue], [selectedItem]);
      setSheetOpen(false);
    }
  }, [mode, getLabel, onChange]);

  // Mobile: Apply selection and close
  const handleMobileDone = useCallback(() => {
    const selectedItems = localValue.map((val) => ({
      value: val,
      label: getLabel(val),
    }));
    onChange(localValue, selectedItems);
    setSheetOpen(false);
  }, [localValue, getLabel, onChange]);

  // Mobile: Handle scroll for infinite loading
  const handleMobileScroll = useCallback((e) => {
    if (!infinite || !hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollTop + clientHeight >= scrollHeight - 50) {
      const nextPage = currentPage + 1;
      fetchResults(mobileSearchValue, nextPage, true);
    }
  }, [infinite, hasMore, loading, currentPage, mobileSearchValue, fetchResults]);

  // Mobile: Get display text
  const mobileDisplayValue = useMemo(() => {
    const valueArray = Array.isArray(value) ? value : (value ? [value] : []);
    if (valueArray.length === 0) return '';
    if (valueArray.length === 1) {
      const displayLabel = getLabel(valueArray[0]);
      return stripHtml(String(displayLabel));
    }
    return t('common|{{count}} sélectionné(s)', { count: valueArray.length });
  }, [value, getLabel, t]);

  const isMultiple = mode === 'multiple';
  const selectedCount = localValue.length;

  // Mobile render
  if (isMobile) {
    return (
      <div className="w-full space-y-2">
        <div className="flex items-center justify-between">
          <label className="block text-xs font-medium text-gray-700">{t(label)}</label>
          {showLanguageSelector && languageFields && languageLabels && (
            <LanguageSelector
              selectedLanguage={selectedLanguage}
              languageLabels={languageLabels}
              onChange={handleLanguageChange}
            />
          )}
        </div>

        {/* Mobile trigger */}
        <div
          onClick={() => setSheetOpen(true)}
          className="w-full min-h-[44px] px-3 py-2 border border-gray-300 rounded-lg bg-white cursor-pointer flex items-center justify-between"
        >
          <span className={`text-xs flex-1 truncate ${mobileDisplayValue ? 'text-gray-900' : 'text-gray-400'}`}>
            {mobileDisplayValue || placeholder || t('common|Search...')}
          </span>
          {loading ? (
            <Spin size="small" className="ml-2" />
          ) : (
            <ChevronDown size={16} className="ml-2 text-gray-400 flex-shrink-0" />
          )}
        </div>

        {/* Mobile sheet */}
        <MobileSheetBase
          isOpen={sheetOpen}
          onClose={() => setSheetOpen(false)}
          title={isMultiple && selectedCount > 0 ? `${t(label)} (${selectedCount})` : t(label)}
          showDone={isMultiple}
          onDone={handleMobileDone}
          doneLabel={t('common|Terminé')}
          doneDisabled={isMultiple && selectedCount === 0}
        >
          {/* Language selector */}
          {showLanguageSelector && languageFields && languageLabels && (
            <div className="px-3 py-2 border-b border-gray-100">
              <LanguageSelector
                selectedLanguage={selectedLanguage}
                languageLabels={languageLabels}
                onChange={handleLanguageChange}
              />
            </div>
          )}

          {/* Search */}
          <div className="sticky top-0 px-3 py-2 bg-white border-b border-gray-100 z-10 flex-shrink-0">
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              <input
                type="text"
                className="w-full h-10 pl-9 pr-3 border border-gray-300 rounded-lg text-sm outline-none focus:border-[#207d54]"
                placeholder={t('common|Rechercher...')}
                value={mobileSearchValue}
                onChange={handleMobileSearchChange}
              />
            </div>
          </div>

          {/* Options list */}
          <div
            className="flex-1 overflow-y-auto overscroll-contain"
            onScroll={handleMobileScroll}
          >
            {/* Loading state */}
            {searching && options.length === 0 && (
              <div className="flex items-center justify-center py-8">
                <Spin size="default" />
              </div>
            )}

            {/* Empty state */}
            {!searching && options.length === 0 && (
              <div className="flex flex-col items-center justify-center py-12 text-gray-400 text-center">
                <div className="text-xs">{t('common|Aucun résultat trouvé')}</div>
              </div>
            )}

            {/* Options */}
            {options.length > 0 && (
              <div className="py-1">
                {options.map((option) => {
                  const isSelected = localValue.includes(option.value);
                  return (
                    <div
                      key={option.value}
                      className={`flex items-center min-h-[44px] px-3 py-2 cursor-pointer ${isSelected ? 'bg-green-50' : ''}`}
                      onClick={() => handleMobileSelect(option.value)}
                    >
                      {isMultiple ? (
                        <div
                          className={`w-4 h-4 border-2 rounded mr-3 flex-shrink-0 flex items-center justify-center ${
                            isSelected ? 'bg-[#207d54] border-[#207d54]' : 'border-gray-300'
                          }`}
                        >
                          {isSelected && <Check size={10} color="#fff" />}
                        </div>
                      ) : (
                        <div
                          className={`w-4 h-4 border-2 rounded-full mr-3 flex-shrink-0 flex items-center justify-center ${
                            isSelected ? 'border-[#207d54]' : 'border-gray-300'
                          }`}
                        >
                          {isSelected && <div className="w-2 h-2 bg-[#207d54] rounded-full" />}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="text-xs text-gray-900">
                          <HtmlContent html={option.label} />
                        </div>
                        {showCount && option.data?.count && (
                          <div className="text-xs text-gray-500">{option.data.count} résultats</div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Loading more */}
            {loading && options.length > 0 && (
              <div className="flex items-center justify-center py-4">
                <Spin size="small" />
              </div>
            )}
          </div>
        </MobileSheetBase>
      </div>
    );
  }

  // Desktop render
  return (
    <div className="elasticsearch-select-container">
      <div className="select-header">
        <label className="select-label">{t(label)}</label>
        {showLanguageSelector && languageFields && languageLabels && (
          <LanguageSelector
            selectedLanguage={selectedLanguage}
            languageLabels={languageLabels}
            onChange={handleLanguageChange}
          />
        )}
      </div>

      <Select
        ref={ref}
        key={`${field}-${selectedLanguage}`}
        className="elasticsearch-select"
        mode={mode}
        labelInValue={false}
        filterOption={false}
        onSearch={debouncedSearch}
        onDropdownVisibleChange={handleDropdownVisibleChange}
        onPopupScroll={infinite ? handleScroll : undefined}
        placeholder={t('common|Search...') || TRANSLATIONS.SEARCH}
        notFoundContent={
          searching ? (
            <Spin size="small" />
          ) : (
            t('common|No results found') || TRANSLATIONS.NO_RESULTS
          )
        }
        loading={searching}
        value={value}
        onChange={handleChange}
        showSearch
        autoClearSearchValue
        options={selectOptions}
        optionRender={(option) => (
          <OptionRenderer option={option} showCount={showCount} />
        )}
        maxTagTextLength={40}
        tagRender={(tagProps) => {
          const { value: tagValue, closable, onClose, isMaxTag, label: tagLabel } = tagProps;

          // Overflow "+N" indicator when maxTagCount is exceeded
          if (isMaxTag) {
            return <Tag style={{ marginRight: 3 }}>{tagLabel}</Tag>;
          }

          const displayLabel = getLabel(tagValue);
          const cleanLabel = stripHtml(String(displayLabel || tagValue || ''));
          const truncatedLabel = cleanLabel.length > 15
            ? `${cleanLabel.substring(0, 15)}...`
            : cleanLabel;
          return (
            <Tooltip title={cleanLabel}>
              <Tag
                closable={closable}
                onClose={onClose}
                style={{ marginRight: 3 }}
              >
                {truncatedLabel}
              </Tag>
            </Tooltip>
          );
        }}
        suffixIcon={<img src={DownIcon} alt="Down" className="down-icon" />}
        {...props}
      />

      {showSelectedTags && (
        <CollapsibleTags
          value={value}
          getLabel={getLabel}
          onRemove={handleRemoveTag}
          maxVisible={maxVisibleTags}
        />
      )}
    </div>
  );
});

ElasticsearchSelect.displayName = 'ElasticsearchSelect';

export default React.memo(ElasticsearchSelect);
